import { ReactNode } from 'react';
import { Grid, Typography, Card, Box, Divider, Button, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TextField, useRecordContext } from "react-admin";
import { IfCanAccess } from "@react-admin/ra-rbac";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import AnimatedBlock from '../../../components/Common/AnimatedBlock';
import InfoRow from '../../../components/Common/InfoRow';
import Label from '../../../components/Label';

const Section = ({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) => (
  <Box mb={3}>
    <Box pb={2}>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
    <AnimatedBlock>
      <Card>
        <Box px={2} py={1}>
          {children}
        </Box>
      </Card>
    </AnimatedBlock>
  </Box>
);

function ParametersGasStationTab() {
  const { t }: { t: any } = useTranslation();
  const project = useRecordContext();
  const coinhouseCounterUrl =
    process.env.NODE_ENV === "production"
      ? "https://counter.coinhouse.com"
      : "https://counter.comptoirdubitcoin.com";
  const allowedAddresses = project?.parameters?.gasStation?.allowedAddresses || [];
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Section title={t('Gas station configuration')} subtitle={t('Currency, limits and destination rules of this gas station')}>
          <InfoRow label={t('Currency')}>
            <TextField source="parameters.gasStation.currency" />
          </InfoRow>
          <InfoRow label={t('Volume available per 24 hours')}>
            <TextField source="parameters.gasStation.limitPer24H" />
          </InfoRow>
          <InfoRow label={t('Webhook URL')}>
            <TextField source="parameters.webhookUrl" sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 400 }} />
          </InfoRow>
          <Divider sx={{ my: 1 }} />
          <InfoRow label={t('Approval threshold (EUR)')}>
            <TextField source="parameters.gasStation.approvalThresholdEur" />
          </InfoRow>
          <InfoRow label={t('Commission rate')}>
            <TextField source="parameters.gasStation.commissionRate" />
          </InfoRow>
          <InfoRow label={t('Destination mode')}>
            <TextField source="parameters.gasStation.destinationMode" sx={{ textTransform: "capitalize" }} />
          </InfoRow>
          <InfoRow label={t('Allowed addresses')}>
            <Box component="span" sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 400, wordBreak: "break-all" }}>
              {allowedAddresses.join(', ') || '—'}
            </Box>
          </InfoRow>
        </Section>
        <IfCanAccess resource="projects.userIdCNHS" action="show">
          <Section title={t("Coinhouse configuration")} subtitle={t("The Coinhouse user this entity is linked to")}>
            <InfoRow label={t("Coinhouse user UUID")}>
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <TextField source="userIdCNHS" sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 400 }} />
                <Link
                  href={`${coinhouseCounterUrl}/coinhouse-counter/${project.userIdCNHS}`}
                  target="_blank"
                >
                  <Button size="small" variant="text" startIcon={<OpenInNewRoundedIcon />}>
                    {t("Coinhouse Counter")}
                  </Button>
                </Link>
              </Box>
            </InfoRow>

            <IfCanAccess resource="projects.fields.autorizedByCNHS" action="show">
              <InfoRow label={t("Authorized by Coinhouse")}>
                <Label color={project.autorizedByCNHS ? "success" : "secondary"}>
                  {project.autorizedByCNHS ? t("Yes") : t("No")}
                </Label>
              </InfoRow>
            </IfCanAccess>
          </Section>
        </IfCanAccess>
      </Grid>
    </Grid>
  );
}

export default ParametersGasStationTab;
